import type { ContentSegment, ParsedTool } from "@/components/aios-command/chat/message-parser";

export type SkillChipVariant = "using" | "preparing";

export type SkillDisplayPiece =
  | { kind: "segment"; segment: ContentSegment; index: number }
  | { kind: "skill"; variant: SkillChipVariant; name: string; key: string };

function toolName(type: string): string {
  return type.replace(/^tool[-_]?/i, "").toLowerCase().replace(/-/g, "_");
}

function stringField(record: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  const args = record.args;
  if (args && typeof args === "object") {
    const nested = args as Record<string, unknown>;
    for (const key of keys) {
      const value = nested[key];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
  }
  return null;
}

function skillNameFromPath(filePath: string): string | null {
  if (!filePath.startsWith("/skills/")) return null;
  const name = filePath.slice("/skills/".length).split("/")[0];
  return name || null;
}

/** Friendly skill indicator for a tool call, or null when it should render normally. */
export function classifySkillTool(
  tool: ParsedTool,
): { kind: "prepare"; name: string } | { kind: "read"; name: string; skillMd: boolean } | { kind: "hide" } | null {
  const name = toolName(tool.type);
  const input =
    tool.input && typeof tool.input === "object" ? (tool.input as Record<string, unknown>) : {};

  if (name === "aios_prepare_skill" || name.endsWith("prepare_skill")) {
    return { kind: "prepare", name: stringField(input, ["name"]) || "skill" };
  }

  if (name !== "read_file" && name !== "readfile") return null;

  const filePath = stringField(input, ["file_path", "path", "filePath"]);
  if (!filePath || !filePath.startsWith("/skills/")) return null;
  const skillName = skillNameFromPath(filePath);
  if (!skillName) return { kind: "hide" };
  return {
    kind: "read",
    name: skillName,
    skillMd: filePath.endsWith("/SKILL.md"),
  };
}

/**
 * Collapse consecutive skill file reads into one chip.
 * A SKILL.md read becomes "Using skill". Other files in that run are not separate rows.
 * Preparing a skill stays its own chip.
 */
export function collapseSkillDisplay(segments: ContentSegment[]): SkillDisplayPiece[] {
  const pieces: SkillDisplayPiece[] = [];
  let index = 0;
  while (index < segments.length) {
    const segment = segments[index];
    const info = segment.type === "tool" && segment.tool ? classifySkillTool(segment.tool) : null;
    if (!info) {
      pieces.push({ kind: "segment", segment, index });
      index += 1;
      continue;
    }
    if (info.kind === "hide") {
      index += 1;
      continue;
    }
    if (info.kind === "prepare") {
      pieces.push({
        kind: "skill",
        variant: "preparing",
        name: info.name,
        key: segment.tool?.toolCallId || `prepare-${index}`,
      });
      index += 1;
      continue;
    }

    let sawSkillMd = info.skillMd;
    const key = segment.tool?.toolCallId || `using-${index}`;
    let next = index + 1;
    while (next < segments.length) {
      const following = segments[next];
      const followingInfo =
        following.type === "tool" && following.tool ? classifySkillTool(following.tool) : null;
      if (!followingInfo || followingInfo.kind !== "read" || followingInfo.name !== info.name) break;
      sawSkillMd = sawSkillMd || followingInfo.skillMd;
      next += 1;
    }
    if (sawSkillMd) {
      pieces.push({ kind: "skill", variant: "using", name: info.name, key });
    }
    index = next;
  }
  return pieces;
}
